import { useEffect } from "react";
import { useModelStore } from "../store/useModelStore";
import { useSupportStore } from "../store/useSupportStore";
import { useBeamTrussStore } from "../../src/hooks/useBeamTrussManagement";
import { usePropertyStore } from "../store/usePropertyStore";
import { useBeamSectionStore } from "../hooks/useBeamSection";

export const useDeleteKeyHandler = () => {
  const {
    selectedNodeIds,
    selectedBeamIds,
    deleteNode,
    deleteBeam,
    beams,
    clearSelection,
  } = useModelStore();

  const { supports, removeSupport } = useSupportStore();
  const { beamTrusses, removeBeamTruss } = useBeamTrussStore();
  const { properties, deleteProperty } = usePropertyStore();
  const beamSectionStore = useBeamSectionStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") {
        return;
      }

      const target = event.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return;
      }

      event.preventDefault();

      if (selectedNodeIds.length === 0 && selectedBeamIds.length === 0) {
        return;
      }

      const deletedItems = {
        nodes: 0,
        beams: 0,
        supports: 0,
        beamLoads: 0,
        properties: 0,
        sections: 0,
      };

      const allBeamIdsToDelete = new Set<string>(selectedBeamIds);

      selectedNodeIds.forEach((nodeId) => {
        const connectedBeams = beams.filter(
          (beam) => beam.startNodeId === nodeId || beam.endNodeId === nodeId
        );
        connectedBeams.forEach((beam) => allBeamIdsToDelete.add(beam.id));
      });

      const propertiesToCheck = new Set<string>();
      allBeamIdsToDelete.forEach((beamId) => {
        const beam = beams.find((b) => b.id === beamId);
        if (beam && beam.P_ID && beam.P_ID !== "-") {
          propertiesToCheck.add(beam.P_ID);
        }
      });

      selectedNodeIds.forEach((nodeId) => {
        const nodeSupports = supports.filter(
          (support) => support.nodeId === nodeId
        );

        nodeSupports.forEach((support) => {
          removeSupport(support.id);
          deletedItems.supports++;
        });
      });

      allBeamIdsToDelete.forEach((beamId) => {
        const loadsToDelete: any[] = [];
        beamTrusses.forEach((trusses, bId) => {
          if (bId === beamId) {
            loadsToDelete.push(...trusses);
          }
        });

        loadsToDelete.forEach((load) => {
          removeBeamTruss(load.id);
          deletedItems.beamLoads++;
        });
      });

      allBeamIdsToDelete.forEach((beamId) => {
        deleteBeam(beamId);
        deletedItems.beams++;
      });

      selectedNodeIds.forEach((nodeId) => {
        deleteNode(nodeId);
        deletedItems.nodes++;
      });

      const remainingBeams = useModelStore.getState().beams;

      propertiesToCheck.forEach((propertyId) => {
        const stillInUse = remainingBeams.some((b) => b.P_ID === propertyId);

        if (!stillInUse) {
          try {
            deleteProperty(propertyId);
            deletedItems.properties++;
          } catch (error) {
            // console.error(` Failed to delete property:`, error);
          }

          try {
            beamSectionStore.removeSection(propertyId);
            deletedItems.sections++;
          } catch (error) {
            // console.error(`Failed to delete section:`, error);
          }
        } else {
          const usingBeams = remainingBeams
            .filter((b) => b.P_ID === propertyId)
            .map((b) => b.id);
          console.log(
            `  ℹ️ Keeping property ${propertyId} (used by beams: ${usingBeams.join(", ")})`
          );
        }
      });

      // Clear selection
      clearSelection();

      const totalDeleted = Object.values(deletedItems).reduce(
        (a, b) => a + b,
        0
      );
      if (totalDeleted > 0) {
        const summary = [];
        if (deletedItems.nodes > 0)
          summary.push(`${deletedItems.nodes} node(s)`);
        if (deletedItems.beams > 0)
          summary.push(`${deletedItems.beams} beam(s)`);
        if (deletedItems.supports > 0)
          summary.push(`${deletedItems.supports} support(s)`);
        if (deletedItems.beamLoads > 0)
          summary.push(`${deletedItems.beamLoads} load(s)`);
        if (deletedItems.properties > 0)
          summary.push(`${deletedItems.properties} propert(ies)`);
        if (deletedItems.sections > 0)
          summary.push(`${deletedItems.sections} section(s)`);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedNodeIds,
    selectedBeamIds,
    beams,
    supports,
    beamTrusses,
    properties,
    beamSectionStore,
    deleteNode,
    deleteBeam,
    removeSupport,
    removeBeamTruss,
    deleteProperty,
    clearSelection,
  ]);
};
